import React, { useState, useEffect } from "react";
import { Typography, Button, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  CustomDatePicker,
  MultiSelectInput,
  CustomFormControl,
} from "_components";
import Popper from "@mui/material/Popper";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { alertActions } from "_store";
import { useDispatch, useSelector } from "react-redux";
import {
  getMCReqTypeApi,
  getRequestTypeApi,
  getStateApi,
  getStatusApi,
} from "services/mapCenter/mapCenter.thunks";
import { ERR_MSG } from "constants/labels";

const Filter = ({
  companyList,
  handleFilterSubmit,
  filterValues = {},
  setRowSelection = () => {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const id = isOpen && Boolean(anchorEl) ? "simple-popper" : undefined;
  const header = "Map Request";

  const authUser = useSelector((x) => x.auth?.value);
  const user = authUser?.Data;
  const userAccess = user?.UserAccess;
  const userdetails = user?.UserDetails;
  const isMCUserExpired = userdetails?.Status === "Expired";
  const isMCUserPartiallyApproved =
    userdetails?.Status === "Partially Approved";
  const isMCUserPartiallyVerified = userdetails?.Status === "Partially Verified";
  const isAdmin = userAccess?.some((access) =>
    access.Role.toLowerCase().includes("admin")
  );

  const dispatch = useDispatch();

  const [stateValues, setStateValues] = useState([]);
  const [reqType, setReqType] = useState([]);
  const [requestTypeValues, setRequestTypeValues] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [statusData, setStatusData] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedMapRequestType, setSelectedMapRequestType] = useState("");
  const [selectedStateCode, setSelectedStateCode] = useState("");
  const [selectedRequestType, setSelectedRequestType] = useState("");

  const fetchRequestType = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getMCReqTypeApi()).unwrap();
      const reqTypes = res?.Data?.map((data) => {
        return { label: data?.Name, value: data?.Name };
      });
      setReqType(reqTypes);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const fetchRequestorType = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getRequestTypeApi()).unwrap();
      const reqTypes = res?.Data?.map((data) => {
        return { label: data?.Name, value: data?.Name };
      });
      setRequestTypeValues(reqTypes);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const fetchState = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getStateApi()).unwrap();
      const states = res?.Data?.map((data) => {
        return { label: data?.Jurisdiction, value: data?.Jurisdiction };
      });
      setStateValues(states);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const fetchStatus = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getStatusApi()).unwrap();
      const status = res?.Data?.map((data) => {
        return { label: data?.StatusName, value: data?.StatusName };
      });
      setStatusData(status);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const defaultValues = isAdmin
    ? {
        MapRequestType: "",
        DateAdded: null,
        Status: "",
        StateCode: "",
        RequestType: "",
        RequestorName: "",
        Company: "",
      }
    : {
        DateAdded: null,
        Status: "",
      };

  const {
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    setValue,
    register,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  const watchedData = isAdmin
    ? [
        "Status",
        "DateAdded",
        "StateCode",
        "MapRequestType",
        "RequestType",
        "RequestorName",
        "Company",
      ]
    : ["Status", "DateAdded"];
  const watchedValues = watch(watchedData);

  const setMultiselectRawValue = (arrVal) =>
    Array.isArray(arrVal) ? arrVal.join(",") : "";

  useEffect(() => {
    if (!isMCUserExpired && !isMCUserPartiallyApproved && !isMCUserPartiallyVerified) {
      fetchRequestType();
      fetchState();
      fetchRequestorType();
      fetchStatus();
      reset(defaultValues);
    }
  }, [isAdmin, reset]);

  useEffect(() => {
    if (filterValues && Object.keys(filterValues).length > 0) {
      reset((prev) => ({
        ...prev,
        ...filterValues,
        DateAdded: filterValues.DateAdded
          ? dayjs(filterValues.DateAdded)
          : null,
      }));
      const { MapRequestType, MapCenterRequestType, StateCode, Status } =
        filterValues;
      setSelectedRequestType(setMultiselectRawValue(MapCenterRequestType));
      setSelectedMapRequestType(setMultiselectRawValue(MapRequestType));
      setSelectedStateCode(setMultiselectRawValue(StateCode));
      setSelectedStatus(setMultiselectRawValue(Status));
    } else if (filterValues === null) {
      handleReset();
    }
  }, [filterValues, reset]);

  const onSubmit = async (data) => {
    dispatch(alertActions.clear());
    try {
      const statusArray = data.Status ? data.Status.split(",") : null;
      const stateCodeArray = data.StateCode ? data.StateCode.split(",") : null;
      const mapRequestTypeArray = data.MapRequestType
        ? data.MapRequestType.split(",")
        : null;
      const requestTypeArray = data.RequestType
        ? data.RequestType.split(",")
        : null;

      const payload = isAdmin
        ? {
            Status: statusArray,
            DateAdded: data.DateAdded
              ? dayjs(data.DateAdded)
                  .startOf("month")
                  .format("YYYY-MM-DDTHH:mm:ss")
              : null,
            StateCode: stateCodeArray,
            MapRequestType: mapRequestTypeArray,
            MapCenterRequestType: requestTypeArray,
            RequestorName: data.RequestorName || null,
            Company: data.Company || null,
          }
        : {
            Status: statusArray,
            DateAdded: data.DateAdded
              ? dayjs(data.DateAdded)
                  .startOf("month")
                  .format("YYYY-MM-DDTHH:mm:ss")
              : null,
          };

      handleFilterSubmit(payload);
      setIsOpen(false);
      setRowSelection({});
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Fetch Failed",
        })
      );
    }
  };

  const handleReset = () => {
    reset(defaultValues);
    setSelectedStatus("");
    setSelectedMapRequestType("");
    setSelectedStateCode("");
    setSelectedRequestType("");
  };

  useEffect(() => {
    const hasValue = Object.values(watchedValues).some(
      (val) => val !== "" && val !== null && val !== undefined
    );
    setIsFormValid(hasValue);
  }, [watchedValues]);

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    await trigger(fieldName);
  };

  const handleStatusChange = (newValue) => {
    setSelectedStatus(newValue);
    setValue("Status", newValue);
    trigger("Status");
  };

  const handleMapRequestTypeChange = (newValue) => {
    setSelectedMapRequestType(newValue);
    setValue("MapRequestType", newValue);
    trigger("MapRequestType");
  };

  const handleStateCodeChange = (newValue) => {
    setSelectedStateCode(newValue);
    setValue("StateCode", newValue);
    trigger("StateCode");
  };

  const handleRequestTypeChange = (newValue) => {
    setSelectedRequestType(newValue);
    setValue("RequestType", newValue);
    trigger("RequestType");
  };

  return (
    <>
      <Button
        className="Filter"
        type="button"
        variant="contained"
        color="primary"
        aria-describedby={id}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setIsOpen(!isOpen);
        }}
      >
        <FilterListIcon />
        Filter
      </Button>
      <Popper
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        className="Filtercontainer marketerGroup Border"
      >
        <Box
          sx={{ border: 1, p: 1, bgcolor: "background.paper" }}
          className="Filtercontainerinner Border"
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="Registrationcontainer marketerFiltercontainer"
          >
            <Typography component="div" className="userprofilelist">
              <Grid container direction="row" spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12} sm={6} md={6}>
                  <Typography variant="h2" className="userprofilelistcontent">
                    Map Center
                  </Typography>
                </Grid>
              </Grid>
            </Typography>
            {!isAdmin && (
              <>
                <CustomDatePicker
                  name="DateAdded"
                  label="Created date"
                  minDate={dayjs().subtract(12, "month")}
                  maxDate={dayjs().add(0, "month")}
                  control={control}
                  views={["year", "month"]}
                />
                <MultiSelectInput
                  id="status"
                  control={control}
                  name="Status"
                  label="Status"
                  options={statusData}
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  error={!!errors.Status}
                  helperText={errors.Status?.message}
                  handleBlur={handleBlur}
                  setValue={setValue}
                  trigger={trigger}
                />
              </>
            )}

            {isAdmin && (
              <>
                <MultiSelectInput
                  id="requestType"
                  control={control}
                  name="RequestType"
                  label="Request Type"
                  options={reqType}
                  value={selectedRequestType}
                  onChange={handleRequestTypeChange}
                  error={!!errors.RequestType}
                  helperText={errors.RequestType?.message}
                  handleBlur={handleBlur}
                  setValue={setValue}
                  trigger={trigger}
                />
                <MultiSelectInput
                  id="mapRequestType"
                  control={control}
                  name="MapRequestType"
                  label="Requestor type"
                  options={requestTypeValues}
                  value={selectedMapRequestType}
                  onChange={handleMapRequestTypeChange}
                  error={!!errors.MapRequestType}
                  helperText={errors.MapRequestType?.message}
                  handleBlur={handleBlur}
                  setValue={setValue}
                  trigger={trigger}
                />
                <CustomFormControl
                  required={false}
                  id="RequestorName"
                  label="Requestor Name"
                  type="text"
                  register={register}
                  errors={errors}
                />
                <CustomFormControl
                  required={false}
                  id="Company"
                  label="Company"
                  type="text"
                  register={register}
                  errors={errors}
                />
                <CustomDatePicker
                  name="DateAdded"
                  label="Created date"
                  minDate={dayjs().subtract(12, "month")}
                  maxDate={dayjs().add(0, "month")}
                  control={control}
                  views={["year", "month"]}
                />
                <MultiSelectInput
                  id="status"
                  control={control}
                  name="Status"
                  label="Status"
                  options={statusData}
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  error={!!errors.Status}
                  helperText={errors.Status?.message}
                  handleBlur={handleBlur}
                  setValue={setValue}
                  trigger={trigger}
                />
                <MultiSelectInput
                  id="stateCode"
                  control={control}
                  name="StateCode"
                  label="State"
                  options={stateValues}
                  value={selectedStateCode}
                  onChange={handleStateCodeChange}
                  error={!!errors.StateCode}
                  helperText={errors.StateCode?.message}
                  handleBlur={handleBlur}
                  setValue={setValue}
                  trigger={trigger}
                />
              </>
            )}

            <Typography component="div" className="CreateMarketerbutton">
              <Button
                type="submit"
                variant="contained"
                className="submitbutton"
                color="primary"
                disabled={!isFormValid}
              >
                Search
              </Button>
              <Button
                variant="contained"
                color="red"
                className="cancelbutton"
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                Cancel
              </Button>
            </Typography>
          </form>
        </Box>
      </Popper>
    </>
  );
};

export default Filter;
